/**
 * Define un boton de accion para usar con `<app-button-actions>`.
 * Cada boton se identifica por `action` (string que se emite al hacer click).
 */
export interface ActionButtonDef {
  /** Identificador de la accion, se emite en el evento (action) */
  action: string;
  /** Nombre del icono ng-zorro (ej: 'eye', 'edit', 'delete') */
  icon: string;
  /** Texto del tooltip. Si no se define, usa `action` como fallback */
  tooltip?: string;
  /** Color hex del boton (borde e icono). Default: '#1890ff' */
  color?: string;
  /** Funcion que recibe la fila y retorna si el boton es visible. Default: siempre visible */
  visible?: (row: any) => boolean;
  /** Funcion que recibe la fila y retorna si el boton esta deshabilitado. Default: habilitado */
  disabled?: (row: any) => boolean;
  /** ID de etapa destino para acciones de avance de workflow. Ignorado por el componente UI. */
  advanceTo?: number;
}

/**
 * Presets con icono, tooltip y color predefinidos para acciones comunes.
 * Colores con contraste >=3:1 sobre blanco (WCAG 1.4.11), homologados con la
 * botonera de FFacturacion (referencia visual de la plataforma). Para acciones
 * custom usar tonos "-7" de la paleta Ant Design (ej. naranja #d46b08,
 * cyan #08979c, morado #722ed1) por la misma razon.
 */
export const ACTION_PRESETS: Record<string, Omit<ActionButtonDef, 'action'>> = {
  ver:    { icon: 'eye',    tooltip: 'Ver',      color: '#1890ff' },
  editar: { icon: 'edit',   tooltip: 'Editar',   color: '#389e0d' },
  borrar: { icon: 'delete', tooltip: 'Eliminar', color: '#ff4d4f' },
};

/**
 * Crea un `ActionButtonDef` a partir de un preset o desde cero.
 * Si `action` coincide con una key de `ACTION_PRESETS` (ver, editar, borrar),
 * se usan sus valores por defecto; si no, solo se requiere que el icono
 * venga en `overrides` o se usa `action` como nombre de icono fallback.
 *
 * @example
 * // Usar preset tal cual
 * actionPreset('ver')
 *
 * // Preset con override (ej: ocultar si no esta activo)
 * actionPreset('borrar', { visible: (row) => row.bactivo })
 *
 * // Accion custom sin preset
 * actionPreset('descargar', { icon: 'download', tooltip: 'Descargar PDF', color: '#722ed1' })
 */
export function actionPreset(
  action: string,
  overrides?: Partial<ActionButtonDef>,
): ActionButtonDef {
  return { action, ...(ACTION_PRESETS[action] ?? { icon: action }), ...overrides };
}
